import type { ExplicitAny } from '@/utils/typings';
import type { FormMeta } from 'vee-validate';
import { onMounted, type ComputedRef, type Ref, onUnmounted, computed } from 'vue';
import { confirmationInjectionKeys, ConfirmationModalInjectionKeyType } from './types';
import { injectStrict } from '@/utils/injectStrict';
import { useRouter } from 'vue-router';
import { FormConfirmationModalOptions } from '../forms/types';

// this CONST is used as a hash-value for the Routing, to bypass the modal when navigation away from dirty forms
export const SKIP_FORM_CHECK = 'skipFormCheck';

// this CONST is used as a hash-value prefix for the Routing,
// to bypass the modal when navigation away from dirty forms
export const TAB_PREFIX = 'tab:';

export const useFormConfirmationModal = <FormValues extends Record<string, ExplicitAny>>(
  meta: ComputedRef<FormMeta<FormValues>>,
  isSubmitting: Ref<boolean>,
  confirmationOptions?: FormConfirmationModalOptions,
) => {
  const { openDialog } = injectStrict<ConfirmationModalInjectionKeyType>(
    confirmationInjectionKeys.form,
  );
  const router = useRouter();

  const shouldOpenModal = computed(
    () =>
      !isSubmitting.value &&
      (confirmationOptions?.skipDirtyCheck === 'never' ||
        (confirmationOptions?.skipDirtyCheck !== 'always' && meta.value.dirty)),
  );

  const showModalOnChanges = async (callback: () => void) => {
    if (shouldOpenModal.value) {
      const success = await new Promise<boolean>((resolve) => {
        openDialog(resolve, confirmationOptions);
      });
      if (success) {
        callback();
      }
    } else {
      callback();
    }
  };

  const handleBeforeUnload = async (e: Event) => {
    if (shouldOpenModal.value) {
      e.preventDefault();
      return e.defaultPrevented;
    }
  };

  let removeListener: () => void;
  onMounted(async () => {
    // catch navigating to another page by manually updating url
    window.addEventListener('beforeunload', handleBeforeUnload);

    // catch navigating to another page by router
    removeListener = router.beforeEach(async (to, _from, next) => {
      if (
        shouldOpenModal.value &&
        !to.hash.includes(SKIP_FORM_CHECK) &&
        !to.hash.startsWith(`#${TAB_PREFIX}`)
      ) {
        const confirmed = await new Promise<boolean>((resolve) => {
          openDialog(resolve, confirmationOptions);
        });
        if (!confirmed) {
          return next(false);
        }
      }
      next();
    });
  });

  onUnmounted(async () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    removeListener();
  });

  return { showModalOnChanges };
};
